import { getApiEndpoint } from '../../../../../config/api';
import { getToken } from '../../../../../utils/auth';

/**
 * File upload / download helpers for the contractor Files module.
 * Uploads use multipart/form-data (the browser sets the boundary, so we send
 * only the Authorization header — never Content-Type). Uploads go through
 * XMLHttpRequest so callers can show real progress. Downloads fetch the
 * authenticated stream as a blob and trigger a save.
 *
 * Errors thrown here look like apiFetch errors: `status`, `body` (parsed JSON
 * when the server sent one), `network: true` when the request never landed —
 * so `apiErrorMessage(err)` and `uploadErrorMessage(err)` both work.
 */

/** Server upload limit (backend routes/contractor/files.js MAX_FILE_BYTES). */
export const MAX_UPLOAD_BYTES = 100 * 1024 * 1024;

/** Extensions the Files module accepts (mirrors the backend allowlist). */
export const ACCEPTED_FILE_TYPES = '.pdf,.png,.jpg,.jpeg,.gif,.webp,.avif,.heic,.heif,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.odt,.ods,.rtf,.csv,.txt,.dwg,.dxf,.zip,.msg,.eml,.mp4,.mov';

export const formatBytes = (n) => {
  const v = Number(n) || 0;
  if (v < 1024) return `${v} B`;
  if (v < 1024 * 1024) return `${Math.round(v / 1024)} KB`;
  return `${(v / (1024 * 1024)).toFixed(v < 10 * 1024 * 1024 ? 1 : 0)} MB`;
};

/** Human message for an upload failure, mapped from the backend's error codes. */
export function uploadErrorMessage(err, fileName = 'That file') {
  if (!err) return 'Upload failed — try again';
  const code = err.body?.code || err.body?.error;
  if (code === 'FILE_TOO_LARGE' || err.status === 413) return `${fileName} is too large — the limit is ${formatBytes(MAX_UPLOAD_BYTES)}`;
  if (code === 'FILE_TYPE_NOT_ALLOWED' || err.status === 415) return `${fileName} isn't an allowed file type — upload a PDF, image, Office document, CSV or ZIP`;
  if (code === 'FILE_REQUIRED') return 'Choose a file to upload';
  if (code === 'ENTITY_NOT_FOUND') return 'The record this file belongs to no longer exists';
  if (code === 'NO_COMPANY') return 'Your account isn’t linked to a company yet';
  if (err.network || err.status === 0) return 'Upload failed — check your connection and try again';
  if (err.status === 403) return "You don't have permission to upload here";
  return err.body?.message || 'Upload failed — try again';
}

/** Validate a File before uploading. Returns an error sentence or null. */
export function checkUploadable(file) {
  if (!file) return 'Choose a file to upload';
  if (file.size > MAX_UPLOAD_BYTES) return `${file.name} is ${formatBytes(file.size)} — the limit is ${formatBytes(MAX_UPLOAD_BYTES)}`;
  if (file.size === 0) return `${file.name} is empty`;
  return null;
}

/** Multipart POST via XHR with upload progress. Resolves parsed JSON. */
function sendMultipart(endpoint, fd, { onProgress, method = 'POST', signal } = {}) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open(method, getApiEndpoint(endpoint));
    const token = getToken();
    if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    if (onProgress && xhr.upload) {
      xhr.upload.onprogress = (e) => { if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100)); };
    }
    xhr.onload = () => {
      let body = null;
      try { body = xhr.responseText ? JSON.parse(xhr.responseText) : null; } catch { body = null; }
      if (xhr.status >= 200 && xhr.status < 300) { resolve(body); return; }
      const e = new Error(`Upload failed (${xhr.status})`);
      e.status = xhr.status;
      e.body = body;
      reject(e);
    };
    xhr.onerror = () => { const e = new Error('Network error during upload'); e.status = 0; e.network = true; reject(e); };
    xhr.onabort = () => { const e = new Error('Upload cancelled'); e.name = 'AbortError'; reject(e); };
    if (signal) signal.addEventListener('abort', () => xhr.abort(), { once: true });
    xhr.send(fd);
  });
}

/** POST a real file to /api/contractor/files/upload. `meta` may include title,
 *  document_group, go_live, expire, entity_type, entity_id. Returns the created row.
 *  `opts.onProgress(percent)` reports upload progress. */
export async function uploadFile(file, meta = {}, opts = {}) {
  const fd = new FormData();
  fd.append('file', file);
  Object.entries(meta).forEach(([k, v]) => { if (v != null && v !== '') fd.append(k, v); });
  return sendMultipart('/api/contractor/files/upload', fd, opts);
}

/** Replace a stored file with a new version. Returns `{ id, version, ... }`. */
export async function uploadFileVersion(id, file, opts = {}) {
  const fd = new FormData();
  fd.append('file', file);
  return sendMultipart(`/api/contractor/files/${encodeURIComponent(id)}/versions`, fd, opts);
}

/** POST a Carbone report document template (.docx/.xlsx/.odt/.ods) to the reports store.
 *  Returns the created row { id, name, format, file_name, file_size }. */
export async function uploadReportDocTemplate(file, name = '', opts = {}) {
  const fd = new FormData();
  fd.append('template', file);
  if (name) fd.append('name', name);
  try {
    return await sendMultipart('/api/contractor/report-doc-templates', fd, opts);
  } catch (e) {
    if (e.body?.message) e.message = e.body.message;
    throw e;
  }
}

/** Fetch a stored file with auth and trigger a browser download. */
export async function downloadFile(id, filename = 'file') {
  let res;
  try {
    res = await fetch(getApiEndpoint(`/api/contractor/files/${id}/download`), {
      headers: { Authorization: `Bearer ${getToken()}` },
    });
  } catch (cause) {
    const e = new Error('Download failed'); e.status = 0; e.network = true; e.cause = cause; throw e;
  }
  if (!res.ok) {
    const e = new Error(`Download failed (${res.status})`); e.status = res.status;
    try { e.body = await res.json(); } catch { /* not json */ }
    throw e;
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

/**
 * Signed file links (`…?exp=<unix seconds>&sig=…`) last an hour. True when the link has
 * expired or will within `marginMs` (default 10 min — i.e. it's ~50 minutes old), so the
 * caller should re-read the record for a fresh link before using it. Unsigned → false.
 */
export function signedUrlExpiresSoon(url, marginMs = 10 * 60 * 1000) {
  const m = /[?&]exp=(\d+)/.exec(String(url || ''));
  if (!m) return false;
  return Number(m[1]) * 1000 - Date.now() < marginMs;
}

/** Font Awesome icon + colour for a file, by mime type or extension. */
export function fileIcon(nameOrMime = '') {
  const s = String(nameOrMime).toLowerCase();
  if (/pdf/.test(s)) return { icon: 'fa-solid fa-file-pdf', color: 'var(--cw-danger, #dc2626)' };
  if (/(image|png|jpe?g|gif|webp|svg|heic)/.test(s)) return { icon: 'fa-solid fa-file-image', color: '#7c3aed' };
  if (/(sheet|excel|csv|xlsx?)/.test(s)) return { icon: 'fa-solid fa-file-excel', color: 'var(--cw-success, #059669)' };
  if (/(word|doc|docx)/.test(s)) return { icon: 'fa-solid fa-file-word', color: 'var(--cw-primary, #1A80E5)' };
  if (/(zip|rar|7z|tar|gz)/.test(s)) return { icon: 'fa-solid fa-file-zipper', color: '#b45309' };
  return { icon: 'fa-solid fa-file-lines', color: 'var(--cw-text-faint, #6b7280)' };
}
